import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LINKS, FOUNDERS } from "@/content/site";

const COLS = [
  {
    title: "Company",
    links: [
      { label: "Mission", to: "/" },
      { label: "Manifesto", to: "/manifesto" },
      { label: "Company brief", href: "/company/", local: true },
      { label: "Vessels", to: "/#vessels" },
      { label: "Founders", to: "/#founders" },
      { label: "Demonstrations", to: "/#demonstrations" },
      { label: "Logbook", to: "/logbook" },
    ],
  },
  {
    title: "Work with us",
    links: [
      { label: "Careers", to: "/careers" },
      { label: "Open application", to: "/careers#open-application" },
      { label: "Partner with us", to: "/partners" },
    ],
  },
  {
    title: "Follow",
    links: [
      { label: "Newsletter", to: "/logbook#subscribe" },
      { label: "RSS feed", href: LINKS.rss },
      { label: "Beehiiv archive", href: LINKS.beehiiv },
      ...FOUNDERS.map((f) => ({ label: `${f.name} on LinkedIn`, href: f.links[1].href })),
    ],
  },
];

const FooterLink = ({ link }) => {
  const cls = "text-[15px] text-fg-2 transition-colors hover:text-fg";
  const id = `footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  if (link.href) return <a href={link.href} target={link.local ? undefined : "_blank"} rel={link.local ? undefined : "noopener noreferrer"} className={cls} data-testid={id}>{link.label}</a>;
  return <Link to={link.to} className={cls} data-testid={id}>{link.label}</Link>;
};

const ThinkingShips = () => (
  <div className="rounded-2xl border border-white/10 p-6" data-testid="footer-thinkingships">
    <p className="eyebrow text-fg-3">Operating ships today?</p>
    <p className="mt-3 text-[15px] leading-relaxed text-fg-2">
      ThinkingShips is our product for shipowners and managers running fleets now.
    </p>
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[14px]">
      <a href={LINKS.thinkingships} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg hover:text-signal" data-testid="footer-thinkingships-site">
        thinkingships.ai <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
      <a href={LINKS.thinkingshipsDemo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg-2 hover:text-signal" data-testid="footer-thinkingships-demo">
        Product demo <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  </div>
);

export const Footer = () => (
  <footer className="relative overflow-hidden border-t border-white/[0.06] bg-ink pt-20" data-testid="site-footer">
    <div className="shell">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <p className="max-w-sm text-2xl font-[520] leading-tight tracking-[-0.02em] text-fg">
            We are building autonomous cargo ships.
          </p>
          <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-fg-2">No one onboard. One connected fleet, supervised from shore.</p>
          <div className="mt-8 max-w-sm"><ThinkingShips /></div>
        </div>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8 lg:pl-12">
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="eyebrow text-fg-3">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => <li key={l.label}><FooterLink link={l} /></li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-16 flex flex-col justify-between gap-3 border-t border-white/[0.06] py-6 text-[13px] text-fg-3 sm:flex-row">
        <span>© {new Date().getFullYear()} x1025. All rights reserved.</span>
        <div className="flex flex-wrap gap-x-5 gap-y-2"><a href="/privacy/" className="hover:text-fg">Privacy</a><button type="button" data-analytics-preferences className="hover:text-fg">Analytics preferences</button><span className="mono">Crew onboard: 0</span></div>
      </div>
    </div>
    <div className="pointer-events-none select-none overflow-hidden" aria-hidden="true">
      <div className="translate-y-[18%] text-center font-[620] leading-[0.8] tracking-[-0.07em] text-[34vw] text-white/[0.035]">
        <span className="font-[380]">x</span>1025
      </div>
    </div>
  </footer>
);
